import { connect } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { Star16Filled, LockClosedRegular } from '@fluentui/react-icons';

import * as ComponentFunctions from './Contract.IAImport.Feedback.Functions';
import {
  IA_IMPORT_FEEDBACK_COMMENT_MAX_LENGTH,
  ImportFeedbackRatingScaleItem,
} from '../../../models/Entities/Contracts/ContractIAImportProcess';
import './Contract.IAImport.Feedback.Styles.css';

const MAX_RATING = 5;

interface StarRowProps {
  value: number;
  size: 'sm' | 'lg';
  scale?: ImportFeedbackRatingScaleItem[];
  disabled?: boolean;
  onSelect?: (rating: number) => void;
  onHover?: (rating: number) => void;
}

// Una sola fila de estrellas para los tres usos (resumen, atajo del widget y
// selector del modal). Sin onSelect es lectura: se dibuja con spans para no
// dejar cinco botones inertes en el recorrido de tabulación.
const StarRow = ({ value, size, scale, disabled, onSelect, onHover }: StarRowProps) => {
  const items = scale ?? Array.from({ length: MAX_RATING }, (_, i) => ({ rating: i + 1 } as ImportFeedbackRatingScaleItem));
  const readOnly = !onSelect;

  return (
    <span
      className={'ia-feedback-stars ia-feedback-stars--' + size}
      role={readOnly ? 'img' : 'radiogroup'}
      aria-label={readOnly ? value + ' de ' + MAX_RATING : 'Puntuación'}
      onMouseLeave={onHover ? () => onHover(0) : undefined}
    >
      {items.map(item => {
        // Un único componente de ícono para los dos estados: lo que cambia es
        // el color. Alternar Filled/Regular remontaba los cinco SVG en cada
        // cambio de puntuación sin necesidad.
        const iconClass =
          'ia-feedback-star ' + (item.rating <= value ? 'ia-feedback-star--on' : 'ia-feedback-star--off');

        if (readOnly) return <Star16Filled key={item.rating} className={iconClass} />;

        // Sin `title`: el tooltip nativo del navegador quedaba flotando cuando
        // el click abría el modal. La etiqueta se lee bajo las estrellas.
        const label = item.ratingLabel || item.ratingName || String(item.rating);
        return (
          <button
            key={item.rating}
            type="button"
            role="radio"
            aria-checked={value === item.rating}
            aria-label={label}
            className="ia-feedback-star-btn"
            disabled={disabled}
            // Sin previsualización por foco: al abrir el modal, Fluent enfoca
            // el primer elemento enfocable del diálogo —esta misma estrella— y
            // el foco pisaba la puntuación elegida con un 1. El recorrido con
            // teclado se guía por el aria-label y el anillo de foco.
            onMouseEnter={onHover ? () => onHover(item.rating) : undefined}
            onClick={() => onSelect(item.rating)}
          >
            <Star16Filled className={iconClass} />
          </button>
        );
      })}
    </span>
  );
};

const ContractIAImportFeedbackComponent = (props: ComponentFunctions.Props) => {
  const { process } = props;

  const [open, setOpen] = useState<boolean>(false);
  const [rating, setRating] = useState<number>(0);
  const [hovered, setHovered] = useState<number>(0);
  const [inviteHover, setInviteHover] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  // El éxito vive en el store y sobrevive al cierre del modal, así que cerrar
  // por successRating a secas cerraría también en la próxima apertura. Sólo se
  // reacciona al envío hecho desde esta instancia.
  const [submitted, setSubmitted] = useState<boolean>(false);

  const scale = useMemo(
    () => ComponentFunctions.buildScale(props.ratingScale),
    [props.ratingScale]
  );

  const isRated = ComponentFunctions.isRatedProcess(process);
  const canRate = ComponentFunctions.isRateableProcess(process) && !isRated;
  const isConflict = props.failureRating && props.ratingError?.ErrorCode === 409;

  // La escala se pide una sola vez. También cuando sólo está la fila del
  // widget: ahí ya se muestra la etiqueta del punto apuntado.
  useEffect(() => {
    if (
      (open || canRate) &&
      props.ratingScale.length === 0 &&
      !props.isGettingRatingScale &&
      !props.failOnGettingRatingScale
    ) {
      props.GetImportFeedbackRatingScale();
    }
  }, [open, canRate]);

  // Cierra el modal cuando el envío de esta instancia terminó bien. El 409 no
  // cierra: el usuario tiene que leer por qué se rechazó.
  useEffect(() => {
    if (!submitted) return;
    if (props.successRating === true) {
      setSubmitted(false);
      setOpen(false);
    } else if (props.successRating === false) {
      setSubmitted(false);
    }
  }, [submitted, props.successRating]);

  // El atajo del widget abre el modal con la puntuación ya elegida: la fila de
  // estrellas es el control, no un botón que lleva a otro control.
  const onOpen = (preset: number) => {
    setRating(preset);
    setHovered(0);
    setInviteHover(0);
    setComment('');
    setSubmitted(false);
    setOpen(true);
  };

  const onClose = () => {
    setSubmitted(false);
    setOpen(false);
  };

  const onSubmit = () => {
    if (!process || rating < 1) return;
    setSubmitted(true);
    props.RateImportContractProcess({
      processId: process.id,
      rating: rating,
      feedbackComment: comment.trim() || undefined,
    });
  };

  const selected = hovered || rating;
  const selectedItem = scale.find(item => item.rating === selected);
  const inviteItem = scale.find(item => item.rating === inviteHover);
  const feedbackDate = ComponentFunctions.formatFeedbackDate(process?.feedbackAt);

  if (!process) return null;

  const metaParts = [
    process.feedbackBy ? 'por ' + process.feedbackBy : null,
    feedbackDate,
  ].filter(Boolean);

  return (
    <div className="ia-feedback">

      {/* ── Puntuación registrada ── */}
      {isRated && (
        <div className="ia-feedback-rated">
          <div className="ia-feedback-rated__row">
            <span className="ia-feedback-rated__caption">Tu calificación</span>
            <StarRow value={process.rating ?? 0} size="sm" />
            <span className="ia-feedback-rated__label">
              {process.ratingLabel || process.ratingName || process.rating + '/' + MAX_RATING}
            </span>
          </div>

          {/* Sin recortes: el comentario es lo que el usuario escribió y una
              elipsis lo dejaba ilegible justo donde importa. */}
          {process.feedbackComment && (
            <p className="ia-feedback-rated__comment">{process.feedbackComment}</p>
          )}

          {metaParts.length > 0 && (
            <span className="ia-feedback-rated__meta">
              <LockClosedRegular className="ia-feedback-rated__meta-icon" />
              {metaParts.join(' · ')}
            </span>
          )}
        </div>
      )}

      {/* ── Atajo para calificar ── */}
      {canRate && (
        <div className="ia-feedback-invite">
          {/* La etiqueta del punto apuntado reemplaza a la pregunta mientras se
              recorre la fila. Los dos textos se apilan en la misma celda y el
              inactivo se oculta sin dejar de ocupar lugar: si se reemplazara el
              contenido, la fila tomaría el ancho del texto corto y la tarjeta
              entera se angostaría con el mouse encima. */}
          <span className="ia-feedback-invite__labels">
            <span
              className={
                'ia-feedback-invite__label' + (inviteItem ? ' ia-feedback-invite__label--idle' : '')
              }
            >
              ¿Cómo resultó la importación?
            </span>
            <span
              className={
                'ia-feedback-invite__label ia-feedback-invite__label--hint' +
                (inviteItem ? '' : ' ia-feedback-invite__label--idle')
              }
            >
              {inviteItem
                ? inviteItem.ratingLabel || inviteItem.ratingName || inviteHover + '/' + MAX_RATING
                : ''}
            </span>
          </span>
          <StarRow
            value={inviteHover}
            size="sm"
            scale={scale}
            onHover={setInviteHover}
            onSelect={onOpen}
          />
        </div>
      )}

      {/* ── Modal de feedback ── */}
      <Dialog
        open={open}
        modalType="modal"
        onOpenChange={(_, data) => {
          if (!data.open) onClose();
        }}
      >
        <DialogSurface className="ia-feedback-dialog">
          <DialogBody>
            <DialogTitle className="ia-feedback-dialog__title">Calificar la importación</DialogTitle>

            <DialogContent className="ia-feedback-dialog__content">
              <p className="ia-feedback-dialog__subtitle">
                Contanos qué tan bien interpretó la IA los archivos de este contrato.
              </p>

              <div className="ia-feedback-picker">
                <StarRow
                  value={selected}
                  size="lg"
                  scale={scale}
                  disabled={props.isRating}
                  onHover={setHovered}
                  onSelect={setRating}
                />

                {/* Alto fijo: el significado cambia con cada estrella y sin
                    reservar el espacio el modal salta con el mouse encima. */}
                <div className="ia-feedback-picker__meaning">
                  {selectedItem ? (
                    <>
                      <span className="ia-feedback-picker__label">
                        {selectedItem.ratingLabel ||
                          selectedItem.ratingName ||
                          selectedItem.rating + '/' + MAX_RATING}
                      </span>
                      {selectedItem.ratingMeaning && (
                        <span className="ia-feedback-picker__desc">{selectedItem.ratingMeaning}</span>
                      )}
                    </>
                  ) : (
                    <span className="ia-feedback-picker__placeholder">
                      Elegí una puntuación de 1 a {MAX_RATING}
                    </span>
                  )}
                </div>

                {props.isGettingRatingScale && props.ratingScale.length === 0 && (
                  <span className="ia-feedback-picker__loading">
                    <Spinner size="extra-tiny" />
                    Cargando escala...
                  </span>
                )}
              </div>

              <div className="ia-feedback-comment">
                <div className="ia-feedback-comment__head">
                  <label className="ia-feedback-comment__label" htmlFor="ia-feedback-comment">
                    Comentario
                    <span className="ia-feedback-comment__optional">opcional</span>
                  </label>
                  <span className="ia-feedback-comment__counter">
                    {comment.length}/{IA_IMPORT_FEEDBACK_COMMENT_MAX_LENGTH}
                  </span>
                </div>
                <Textarea
                  id="ia-feedback-comment"
                  rows={3}
                  resize="vertical"
                  value={comment}
                  disabled={props.isRating}
                  maxLength={IA_IMPORT_FEEDBACK_COMMENT_MAX_LENGTH}
                  onChange={(_, d) => setComment(d.value)}
                  placeholder="Ej.: no detectó los conceptos del anexo B, el resto quedó bien."
                />
              </div>

              {props.failureRating && (
                <MessageBar
                  intent={isConflict ? 'warning' : 'error'}
                  className="ia-feedback-dialog__error"
                >
                  <MessageBarBody>
                    <MessageBarTitle>
                      {isConflict
                        ? 'Ya fue calificada'
                        : 'No se pudo registrar la calificación'}
                    </MessageBarTitle>
                    {props.ratingError?.ErrorMessage}
                  </MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>

            <DialogActions className="ia-feedback-dialog__actions">
              {/* La irreversibilidad se dice donde se confirma, no en un cartel
                  al principio que ya quedó scrolleado o leído por encima. */}
              <span className="ia-feedback-dialog__notice">
                <LockClosedRegular className="ia-feedback-dialog__notice-icon" />
                Se registra una sola vez y no puede modificarse
              </span>
              <div className="ia-feedback-dialog__buttons">
                <Button appearance="subtle" onClick={onClose} disabled={props.isRating}>
                  {isConflict ? 'Cerrar' : 'Cancelar'}
                </Button>
                <Button
                  appearance="primary"
                  disabled={rating < 1 || props.isRating || isConflict}
                  onClick={onSubmit}
                >
                  {props.isRating ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

    </div>
  );
};

// El cast va sobre el resultado del connect, no sobre el componente: así el
// consumidor conserva el tipado de `process` en vez de recibir `unknown`.
export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ContractIAImportFeedbackComponent as any) as React.FC<ComponentFunctions.IProps>;
